import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { gamePlayService } from '../../services/db';
import { useAuth } from '../../hooks/useAuth';
import Spinner from '../../components/ui/Spinner';
import Button from '../../components/ui/Button';

const GameAnswersReviewPage = () => {
  const { seasonId, quizId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const stateData = location.state || {};
  const [questions, setQuestions] = useState(stateData.questions || []);
  const [userResponses, setUserResponses] = useState(stateData.userResponses || {});
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(questions.length === 0);

  useEffect(() => {
    if (questions.length === 0) {
      const loadAnswers = async () => {
        setLoading(true);
        try {
          const [quizData, questionList, userRes] = await Promise.all([
            gamePlayService.getQuizDetails(seasonId, quizId),
            gamePlayService.getQuestions(seasonId, quizId),
            user?.uid ? gamePlayService.getUserResult(seasonId, quizId, user.uid) : Promise.resolve(null),
          ]);
          setQuiz(quizData || null);
          setQuestions(questionList || []);
          if (userRes?.userResponses) {
            setUserResponses(userRes.userResponses);
          }
        } catch (err) {
          console.error('Failed to load answers:', err);
        } finally {
          setLoading(false);
        }
      };
      loadAnswers();
    }
  }, [seasonId, quizId, user, questions.length]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '780px', margin: '0 auto', padding: 'var(--sp-6) var(--sp-4)', minHeight: '85vh' }}>
      {/* Top Header matching PageHeader and games module */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '24px' }}>
        <button
          type="button"
          className="page-header__back"
          onClick={() => navigate(`/play/season/${seasonId}/quiz/${quizId}`)}
          aria-label="Back to quiz"
        >
          ←
        </button>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 800, margin: '0 0 4px 0', color: 'var(--clr-text-1)' }}>
            Answer Key & Biblical References
          </h1>
          <p style={{ fontSize: '13.5px', color: 'var(--clr-muted)', margin: 0 }}>
            {quiz?.title ? `Review Scripture answers for "${quiz.title}".` : 'Review the correct Scripture answers and explanations below.'}
          </p>
        </div>
      </div>

      {/* Questions List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {questions.map((q, idx) => {
          const qId = q.question_id || q.id || `q_${idx}`;
          const userAnswer = userResponses[qId];
          const correctAnswer = q.answer;
          const isUserCorrect = userAnswer && String(userAnswer).trim().toLowerCase() === String(correctAnswer).trim().toLowerCase();

          const options = [q.option_1, q.option_2, q.option_3, q.option_4].filter(Boolean);

          return (
            <div
              key={qId}
              className="card"
              style={{
                background: 'var(--clr-surface)',
                border: '1px solid var(--clr-border)',
                borderRadius: '20px',
                padding: '20px',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.03)',
              }}
            >
              {/* Question Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '8px',
                      background: 'var(--clr-background)',
                      color: 'var(--clr-text-1)',
                      fontWeight: 800,
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {idx + 1}
                  </span>
                  {q.verse && (
                    <span style={{ fontSize: '12px', background: '#FFF4F3', color: '#FF5C56', padding: '2px 8px', borderRadius: '6px', fontWeight: 700 }}>
                      📖 {q.verse}
                    </span>
                  )}
                </div>

                {userAnswer && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '3px 8px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 700,
                      background: isUserCorrect ? '#ECFDF5' : '#FEF2F2',
                      color: isUserCorrect ? '#059669' : '#DC2626',
                    }}
                  >
                    {isUserCorrect ? '✓ Correct' : '✕ Missed'}
                  </span>
                )}
              </div>

              {/* Question Text */}
              <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 16px 0', color: 'var(--clr-text-1)', lineHeight: 1.35 }}>
                {q.question}
              </h3>

              {/* Options */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {options.map((opt, optIdx) => {
                  const isCorrect = String(opt).trim().toLowerCase() === String(correctAnswer).trim().toLowerCase();
                  const isChosen = String(opt).trim().toLowerCase() === String(userAnswer || '').trim().toLowerCase();

                  let bg = 'var(--clr-background)';
                  let border = '1px solid var(--clr-border)';
                  let icon = '⚪';

                  if (isCorrect) {
                    bg = '#ECFDF5';
                    border = '1.5px solid #10B981';
                    icon = '✅';
                  } else if (isChosen && !isCorrect) {
                    bg = '#FEF2F2';
                    border = '1.5px solid #EF4444';
                    icon = '❌';
                  }

                  return (
                    <div
                      key={optIdx}
                      style={{
                        padding: '12px 14px',
                        borderRadius: '12px',
                        background: bg,
                        border,
                        fontSize: '14px',
                        fontWeight: isCorrect || isChosen ? 700 : 500,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '10px',
                      }}
                    >
                      <span>{opt}</span>
                      <span style={{ fontSize: '14px' }}>{icon}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: '28px', textAlign: 'center' }}>
        <Button variant="primary" onClick={() => navigate(`/play/season/${seasonId}`)}>
          ← Back to Tournament Rounds
        </Button>
      </div>
    </div>
  );
};

export default GameAnswersReviewPage;
